import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { setActiveAccountId, fetchOrgScopedFor } from '../redux/globalDataSlice';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const norm = (s = '') => s.toString().toLowerCase().normalize('NFKD').replace(/\p{Diacritic}/gu, '');

const AccountPicker = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const person = useSelector((s) => s.globalData.person);
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);
  const accounts = person?.accounts ?? [];

  const selected =
    accounts.find(a => a.accountId === activeAccountId) ||
    accounts.find(a => a.default || a.isDefault || a.defalut) ||
    accounts[0] ||
    null;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen]);

  // Reset search when closed
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!query) return accounts;
    const q = norm(query);
    return accounts.filter(a =>
      norm(a.name || '').includes(q) || norm(a.accountId || '').includes(q)
    );
  }, [accounts, query]);

  const handleSelect = (acc) => {
    setIsOpen(false);
    const id = acc?.accountId ?? null;
    if (!id || id === activeAccountId) return;
    dispatch(setActiveAccountId(id));
    dispatch(fetchOrgScopedFor(id));
    if (location.pathname !== '/') navigate('/');
  };

  if (!accounts.length) return null;

  return (
    <div
      className={classNames(
        'text-gray-400 dark:text-gray-600 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
        'group flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold -mx-2'
      )}
    >
      <UserGroupIcon aria-hidden="true" className="size-6 shrink-0" />

      <div className="flex-1 min-w-0" ref={containerRef}>
        <div className="text-xs font-semibold leading-5 text-gray-400 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black">
          Account
        </div>

        <div className="relative mt-1">
          {/* Trigger */}
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            className={classNames(
              'flex w-full items-center justify-between h-8',
              'text-sm leading-6 font-semibold text-left',
              'text-gray-300 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black',
              'focus:outline-none'
            )}
          >
            <span className="truncate">
              {selected?.name || selected?.accountId || 'Select account…'}
            </span>
            <ChevronUpDownIcon
              className={classNames(
                'size-5 shrink-0 transition-transform',
                'text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-700',
                isOpen ? 'rotate-180' : ''
              )}
            />
          </button>

          {/* Dropdown */}
          <Transition
            as={Fragment}
            show={isOpen}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <div className={classNames(
              'absolute z-10 mt-1 w-full rounded-md shadow-lg ring-1 ring-black/5',
              'bg-gray-900 dark:bg-white'
            )}>
              {/* Search */}
              <div className="relative p-2 border-b border-gray-800 dark:border-gray-200">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 size-3.5 text-gray-500" />
                <input
                  ref={searchRef}
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className={classNames(
                    'w-full rounded py-1 pl-6 text-sm font-semibold bg-transparent',
                    'text-gray-200 dark:text-gray-800',
                    'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                    'focus:outline-none',
                    query ? 'pr-6' : 'pr-2'
                  )}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(''); searchRef.current?.focus(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    <XMarkIcon className="size-3.5 text-gray-500" />
                  </button>
                )}
              </div>

              {/* Options */}
              <ul className="max-h-52 overflow-auto py-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                    No results
                  </li>
                ) : (
                  filtered.map((acc) => {
                    const isActive = acc.accountId === activeAccountId;
                    const label = acc.name || acc.accountId;
                    return (
                      <li
                        key={acc.accountId}
                        onClick={() => handleSelect(acc)}
                        className={classNames(
                          'flex items-center gap-2 px-3 py-1 cursor-pointer',
                          'text-sm leading-6 font-semibold select-none',
                          isActive
                            ? 'text-white dark:text-black'
                            : 'text-gray-300 dark:text-gray-700 hover:bg-gray-800 dark:hover:bg-gray-100 hover:text-white dark:hover:text-black'
                        )}
                      >
                        {isActive
                          ? <CheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          : <span className="h-4 w-4 shrink-0" />
                        }
                        <span className="truncate">{label}</span>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  );
};

export default AccountPicker;
